import React, { forwardRef } from 'react';

import { Dropdown } from '@/lib/base/overlays/dropdown';

interface CardProps {
  variant?: 'default' | 'elevated' | 'glass' | 'interactive';
  as?: 'div' | 'button';
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  topRight?: React.ReactNode;
  topLeft?: React.ReactNode;
  bottomRight?: React.ReactNode;
  bottomLeft?: React.ReactNode;
  actionsMenu?: React.ReactNode;
  onClick?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Polymorphic 'as' prop requires flexible ref type
export const Card = forwardRef<any, CardProps>(
  (
    {
      variant = 'default',
      as: Component = 'div',
      className = '',
      children,
      topRight,
      topLeft,
      bottomRight,
      bottomLeft,
      actionsMenu,
      onClick,
      ...props
    },
    ref
  ) => {
    const variantClasses = {
      default: 'card-refined',
      elevated: 'card-refined-elevated',
      glass: 'card-refined-glass',
      interactive: 'card-refined-interactive',
    };

    const classes = [
      variantClasses[variant as keyof typeof variantClasses],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Component
        ref={ref}
        className={classes}
        {...props}
        onClick={onClick}
        {...(Component === 'button' && { type: 'button' as const })}
        {...(onClick && { role: 'button', tabIndex: 0 })}
      >
        {/* absolute position actions */}
        {topRight && <div className='absolute top-2 right-2'>{topRight}</div>}
        {topLeft && <div className='absolute top-2 left-2'>{topLeft}</div>}
        {bottomRight && (
          <div className='absolute bottom-2 right-2'>{bottomRight}</div>
        )}
        {bottomLeft && (
          <div className='absolute bottom-2 left-2'>{bottomLeft}</div>
        )}

        {/* Actions menu with three-dot button */}
        {actionsMenu && (
          <div className='absolute top-2 right-2'>
            <Dropdown.Root>
              <Dropdown.DotsButton />
              <Dropdown.Popover>
                <Dropdown.Menu>
                  <Dropdown.Section>{actionsMenu}</Dropdown.Section>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </div>
        )}

        {children}
      </Component>
    );
  }
);

Card.displayName = 'Card';

type CardHeaderProps = React.HTMLAttributes<HTMLDivElement>;
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className = '', children, ...props }, ref) => (
    <div
      ref={ref}
      className={`card-refined-header ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  )
);

CardHeader.displayName = 'CardHeader';

type CardBodyProps = React.HTMLAttributes<HTMLDivElement>;
export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className = '', children, ...props }, ref) => (
    <div
      ref={ref}
      className={`card-refined-body ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  )
);

CardBody.displayName = 'CardBody';

type CardFooterProps = React.HTMLAttributes<HTMLDivElement>;
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className = '', children, ...props }, ref) => (
    <div
      ref={ref}
      className={`card-refined-footer ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  )
);

CardFooter.displayName = 'CardFooter';
